import { GameState } from '../game/GameState.js';
import { GameView } from './GameView.js';
import { DragDropHandler } from './DragDropHandler.js';
import { DrawMode, PileType } from '../types/index.js';

export class UIController {
  private gameState: GameState;
  private gameView: GameView;
  private timerInterval: number | null = null;

  // UI Elements
  private newGameBtn: HTMLButtonElement;
  private undoBtn: HTMLButtonElement;
  private redoBtn: HTMLButtonElement;
  private drawModeSelect: HTMLSelectElement;
  private solvableOnlyCheckbox: HTMLInputElement;
  private autoCompleteBtn: HTMLButtonElement;

  constructor() {
    this.gameState = new GameState(DrawMode.THREE_CARD);
    this.gameView = new GameView();

    // Initialize UI elements
    this.newGameBtn = document.getElementById('new-game-btn') as HTMLButtonElement;
    this.undoBtn = document.getElementById('undo-btn') as HTMLButtonElement;
    this.redoBtn = document.getElementById('redo-btn') as HTMLButtonElement;
    this.drawModeSelect = document.getElementById('draw-mode') as HTMLSelectElement;
    this.solvableOnlyCheckbox = document.getElementById('solvable-only') as HTMLInputElement;
    this.autoCompleteBtn = document.getElementById('auto-complete-btn') as HTMLButtonElement;

    // Initialize drag and drop
    new DragDropHandler({
      onMove: this.handleMove.bind(this),
      onStockClick: this.handleStockClick.bind(this),
      onCardDoubleClick: this.handleCardDoubleClick.bind(this)
    });

    this.initializeEventListeners();
    this.loadOrStartNewGame();
  }

  /**
   * Initialize event listeners
   */
  private initializeEventListeners(): void {
    this.newGameBtn.addEventListener('click', () => this.newGame());
    this.undoBtn.addEventListener('click', () => this.undo());
    this.redoBtn.addEventListener('click', () => this.redo());
    this.drawModeSelect.addEventListener('change', () => this.changeDrawMode());
    this.solvableOnlyCheckbox.addEventListener('change', () => this.changeSolvableOnly());
    this.autoCompleteBtn.addEventListener('click', () => this.autoComplete());

    // Keyboard shortcuts
    document.addEventListener('keydown', (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'z' && !e.shiftKey) {
          e.preventDefault();
          this.undo();
        } else if (e.key === 'z' && e.shiftKey || e.key === 'y') {
          e.preventDefault();
          this.redo();
        }
      } else if (e.key === 'n') {
        this.newGame();
      }
    });
  }

  /**
   * Load saved game or start new game
   */
  private loadOrStartNewGame(): void {
    const savedGame = localStorage.getItem('solitaire-game');
    const savedSolvableOnly = localStorage.getItem('solitaire-solvable-only');

    // Load solvable-only preference
    if (savedSolvableOnly !== null) {
      this.solvableOnlyCheckbox.checked = savedSolvableOnly === 'true';
      this.gameState.setSolvableOnly(savedSolvableOnly === 'true');
    }

    if (savedGame) {
      try {
        this.gameState = GameState.deserialize(savedGame);
        this.drawModeSelect.value = this.gameState.getDrawMode().toString();
      } catch (e) {
        console.error('Failed to load saved game:', e);
        this.gameState.newGame();
      }
    } else {
      this.gameState.newGame();
    }

    this.render();
    this.startTimer();
  }

  /**
   * Start a new game
   */
  private newGame(): void {
    if (confirm('Start a new game? Current progress will be lost.')) {
      this.gameState.newGame();
      this.saveGame();
      this.render();
      this.startTimer();
    }
  }

  /**
   * Handle card move
   */
  private handleMove(
    fromPile: PileType,
    fromIndex: number,
    toPile: PileType,
    toIndex: number,
    cardCount: number
  ): boolean {
    const success = this.gameState.moveCards(fromPile, fromIndex, toPile, toIndex, cardCount);

    if (success) {
      this.saveGame();
      this.render();
      this.checkWinCondition();
    }

    return success;
  }

  /**
   * Handle stock pile click (draw cards)
   */
  private handleStockClick(): void {
    this.gameState.drawFromStock();
    this.saveGame();
    this.render();
  }

  /**
   * Handle card double click (auto-move to foundation)
   */
  private handleCardDoubleClick(fromPile: PileType, fromIndex: number): boolean {
    // Can't move from stock
    if (fromPile === PileType.STOCK) {
      return false;
    }

    // Try to move to each foundation
    for (let i = 0; i < 4; i++) {
      const success = this.gameState.moveCards(fromPile, fromIndex, PileType.FOUNDATION, i, 1);
      if (success) {
        this.saveGame();
        this.render();
        this.checkWinCondition();
        return true;
      }
    }

    return false;
  }

  /**
   * Undo last move
   */
  private undo(): void {
    if (this.gameState.undo()) {
      this.saveGame();
      this.render();
    }
  }

  /**
   * Redo last undone move
   */
  private redo(): void {
    if (this.gameState.redo()) {
      this.saveGame();
      this.render();
    }
  }

  /**
   * Change draw mode
   * Disabled once the game has started
   */
  private changeDrawMode(): void {
    const mode = parseInt(this.drawModeSelect.value) as DrawMode;
    const currentMode = this.gameState.getDrawMode();

    // If mode hasn't actually changed, do nothing
    if (mode === currentMode) {
      return;
    }

    // Check if game has been played
    const state = this.gameState.getState();
    const hasPlayed = state.moveCount > 0;

    if (hasPlayed) {
      // Not allowed to change after game has started
      alert('Cannot change draw mode after making moves. Start a new game to change the draw mode.');
      this.drawModeSelect.value = currentMode.toString();
      return;
    }

    // Change mode and start new game
    this.gameState.setDrawMode(mode);
    this.gameState.newGame();
    this.saveGame();
    this.render();
    this.startTimer();
  }

  /**
   * Change solvable-only mode
   * Disabled once the game has started
   */
  private changeSolvableOnly(): void {
    const solvableOnly = this.solvableOnlyCheckbox.checked;
    const currentSolvableOnly = this.gameState.getSolvableOnly();

    // If setting hasn't actually changed, do nothing
    if (solvableOnly === currentSolvableOnly) {
      return;
    }

    // Check if game has been played
    const state = this.gameState.getState();
    const hasPlayed = state.moveCount > 0;

    if (hasPlayed) {
      // Not allowed to change after game has started
      alert('Cannot change this setting after making moves. Start a new game to change the setting.');
      this.solvableOnlyCheckbox.checked = currentSolvableOnly;
      return;
    }

    // Change mode and start new game
    this.gameState.setSolvableOnly(solvableOnly);
    localStorage.setItem('solitaire-solvable-only', solvableOnly.toString());
    this.gameState.newGame();
    this.saveGame();
    this.render();
    this.startTimer();
  }

  /**
   * Auto complete the game
   */
  private autoComplete(): void {
    if (!this.gameState.canAutoComplete()) {
      alert('Auto-complete is only available when all tableau cards are face-up.');
      return;
    }

    // Simple auto-complete: try to move cards to foundations
    let madeMove = true;
    let iterations = 0;
    const maxIterations = 1000; // Prevent infinite loop

    while (madeMove && iterations < maxIterations) {
      madeMove = false;
      iterations++;

      // Try to move from waste to foundation
      const waste = this.gameState.getWaste();
      if (waste.length > 0) {
        for (let i = 0; i < 4; i++) {
          if (this.gameState.moveCards(PileType.WASTE, 0, PileType.FOUNDATION, i, 1)) {
            madeMove = true;
            break;
          }
        }
      }

      // Try to move from tableau to foundation
      if (!madeMove) {
        const tableau = this.gameState.getTableau();
        for (let t = 0; t < tableau.length; t++) {
          if (tableau[t].length > 0) {
            for (let f = 0; f < 4; f++) {
              if (this.gameState.moveCards(PileType.TABLEAU, t, PileType.FOUNDATION, f, 1)) {
                madeMove = true;
                break;
              }
            }
            if (madeMove) break;
          }
        }
      }

      // Draw from stock if no moves found
      if (!madeMove && this.gameState.getStock().length > 0) {
        this.gameState.drawFromStock();
        madeMove = true;
      }
    }

    this.saveGame();
    this.render();
    this.checkWinCondition();
  }

  /**
   * Check if game is won
   */
  private checkWinCondition(): void {
    if (this.gameState.isWon()) {
      this.stopTimer();
      const state = this.gameState.getState();
      const elapsed = Math.floor((Date.now() - state.startTime) / 1000);
      const minutes = Math.floor(elapsed / 60);
      const seconds = elapsed % 60;

      // Start victory celebration animation
      this.playVictoryCelebration();

      setTimeout(() => {
        alert(`Congratulations! You won!\nTime: ${minutes}:${seconds.toString().padStart(2, '0')}\nMoves: ${state.moveCount}`);
      }, 1000);
    }
  }

  /**
   * Play victory celebration animation
   */
  private playVictoryCelebration(): void {
    // Add victory overlay
    const overlay = document.createElement('div');
    overlay.className = 'victory-overlay';
    document.body.appendChild(overlay);

    // Remove overlay after animation
    setTimeout(() => {
      overlay.remove();
    }, 2000);

    // Animate all cards in foundations
    const foundationPiles = document.querySelectorAll('.foundation-pile');
    let cardDelay = 0;

    foundationPiles.forEach((pile) => {
      const cards = pile.querySelectorAll('.card');
      cards.forEach((card) => {
        setTimeout(() => {
          this.animateVictoryCard(card as HTMLElement);
        }, cardDelay);
        cardDelay += 50; // Stagger the animation
      });
    });

    // Add confetti
    this.createConfetti();
  }

  /**
   * Animate a single card for victory celebration
   */
  private animateVictoryCard(card: HTMLElement): void {
    // Random direction and distance
    const angle = Math.random() * Math.PI * 2;
    const distance = 800 + Math.random() * 400;
    const flyX = Math.cos(angle) * distance;
    const flyY = Math.sin(angle) * distance - 200; // Bias upward

    // Random 3D rotation axis
    const rotateX = Math.random();
    const rotateY = Math.random();
    const rotateZ = Math.random();

    // Set CSS custom properties for animation
    card.style.setProperty('--fly-x', `${flyX}px`);
    card.style.setProperty('--fly-y', `${flyY}px`);
    card.style.setProperty('--rotate-x', rotateX.toString());
    card.style.setProperty('--rotate-y', rotateY.toString());
    card.style.setProperty('--rotate-z', rotateZ.toString());

    // Add victory animation class
    card.classList.add('card-victory');

    // Haptic feedback
    if ('vibrate' in navigator) {
      navigator.vibrate(30);
    }
  }

  /**
   * Create confetti particles
   */
  private createConfetti(): void {
    const colors = ['#ffd700', '#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#6c5ce7'];
    const confettiCount = 100;

    for (let i = 0; i < confettiCount; i++) {
      setTimeout(() => {
        const confetti = document.createElement('div');
        confetti.className = 'confetti';

        // Random position across the top of the screen
        confetti.style.left = `${Math.random() * 100}%`;
        confetti.style.top = '-10px';

        // Random color
        const color = colors[Math.floor(Math.random() * colors.length)];
        confetti.style.setProperty('--confetti-color', color);

        // Random size
        const size = 5 + Math.random() * 10;
        confetti.style.width = `${size}px`;
        confetti.style.height = `${size}px`;

        // Random animation delay
        confetti.style.animationDelay = `${Math.random() * 0.5}s`;
        confetti.style.animationDuration = `${2 + Math.random()}s`;

        document.body.appendChild(confetti);

        // Remove after animation
        setTimeout(() => {
          confetti.remove();
        }, 3500);
      }, i * 10);
    }
  }

  /**
   * Render the game state
   */
  private render(): void {
    const state = this.gameState.getState();
    this.gameView.render(state);

    // Update buttons
    this.undoBtn.disabled = !this.gameState.canUndo();
    this.redoBtn.disabled = !this.gameState.canRedo();
    this.autoCompleteBtn.disabled = !this.gameState.canAutoComplete();

    // Disable draw mode and winnable-only settings after first move
    this.drawModeSelect.disabled = state.moveCount > 0;
    this.solvableOnlyCheckbox.disabled = state.moveCount > 0;

    // Update stats
    this.gameView.updateMoveCount(state.moveCount);
    this.gameView.updateTimeElapsed(state.startTime);
  }

  /**
   * Save game to localStorage
   */
  private saveGame(): void {
    try {
      localStorage.setItem('solitaire-game', this.gameState.serialize());
    } catch (e) {
      console.error('Failed to save game:', e);
    }
  }

  /**
   * Start the timer
   */
  private startTimer(): void {
    this.stopTimer();

    this.timerInterval = window.setInterval(() => {
      const state = this.gameState.getState();
      this.gameView.updateTimeElapsed(state.startTime);
    }, 1000);
  }

  /**
   * Stop the timer
   */
  private stopTimer(): void {
    if (this.timerInterval !== null) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }
}
